// EnrichmentPage.jsx
// Composant temporaire pour enrichir les conjugaisons des verbes

import React, { useState, useEffect, useRef } from 'react';
import { Book, Loader2, CheckCircle, AlertCircle, StopCircle } from 'lucide-react';
import './EnrichmentPage.css';

export default function EnrichConjugations() {
  const [verbes, setVerbes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [stopRequested, setStopRequested] = useState(false);
  const shouldStopRef = useRef(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [enrichedVerbs, setEnrichedVerbs] = useState([]);
  const [errors, setErrors] = useState([]);

  // Mapper les codes de langue vers les drapeaux
  const languageFlags = {
    fr: '🇫🇷',
    en: '🇬🇧',
    es: '🇪🇸',
  };

  // Charger les verbes non enrichis au montage du composant
  useEffect(() => {
    loadUnverifiedVerbs();
  }, []);

  // Charger la liste des verbes non enrichis
  const loadUnverifiedVerbs = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/enrichment/verbs');
      const data = await response.json();

      if (data.success) {
        setVerbes(data.data);
      }
    } catch (error) {
      console.error('Erreur chargement verbes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Enrichir un verbe spécifique
  const enrichVerb = async (verbe) => {
    try {
      const response = await fetch(`/api/enrichment/enrich/${verbe.id}`, {
        method: 'POST',
      });

      const data = await response.json();

      if (data.success) {
        return { success: true, verbe };
      } else {
        return { success: false, verbe, error: data.error };
      }
    } catch (error) {
      return { success: false, verbe, error: error.message };
    }
  };

  // Arrêter l'enrichissement
  const stopEnrichment = () => {
    shouldStopRef.current = true;
    setStopRequested(true);
  };

  // Enrichir tous les verbes
  const enrichAllVerbs = async () => {
    if (verbes.length === 0) {
      alert('Aucun verbe à enrichir !');
      return;
    }

    const confirmEnrich = window.confirm(
      `Voulez-vous enrichir ${verbes.length} verbe(s) ?\n\nCela va prendre environ ${Math.ceil(verbes.length * 3)} secondes.\n(~3 secondes par verbe)`
    );

    if (!confirmEnrich) return;

    setEnriching(true);
    shouldStopRef.current = false;
    setStopRequested(false);
    setProgress({ current: 0, total: verbes.length });
    setEnrichedVerbs([]);
    setErrors([]);

    for (let i = 0; i < verbes.length; i++) {
      // Test d'arrêt après chaque verbe
      if (shouldStopRef.current) {
        console.log("Enrichissement arrêté par l'utilisateur");
        break;
      }

      const verbe = verbes[i];

      setProgress({ current: i + 1, total: verbes.length });

      const result = await enrichVerb(verbe);

      if (result.success) {
        setEnrichedVerbs((prev) => [...prev, verbe]);
      } else {
        setErrors((prev) => [...prev, { verbe, error: result.error }]);
      }

      // Petite pause entre chaque appel pour ne pas surcharger l'API
      if (i < verbes.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }

    setEnriching(false);
    shouldStopRef.current = false;
    setStopRequested(false);

    // Recharger la liste des verbes
    await loadUnverifiedVerbs();
  };

  return (
    <div className="enrich-conjugations">
      <div className="enrich-header">
        <div className="enrich-title">
          <Book size={32} color="#4F46E5" />
          <h2>Enrichir les conjugaisons</h2>
        </div>
        <p className="enrich-subtitle">Récupérer toutes les conjugaisons des verbes via l'IA Claude</p>
      </div>

      {/* Statistiques */}
      <div className="enrich-stats">
        <div className="stat-card">
          <div className="stat-value">{verbes.length}</div>
          <div className="stat-label">Verbes à enrichir</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{enrichedVerbs.length}</div>
          <div className="stat-label">Verbes enrichis</div>
        </div>
        <div className="stat-card error">
          <div className="stat-value">{errors.length}</div>
          <div className="stat-label">Erreurs</div>
        </div>
      </div>

      {/* Bouton principal */}
      {!enriching && verbes.length > 0 && (
        <button onClick={enrichAllVerbs} className="btn-enrich" disabled={loading}>
          <Book size={20} />
          Enrichir tous les verbes ({verbes.length})
        </button>
      )}

      {/* Progression avec bouton Stop */}
      {enriching && (
        <div className="progress-container">
          <div className="progress-header">
            <Loader2 size={24} className="spinning" color="#4F46E5" />
            <span>
              Enrichissement en cours... {progress.current} / {progress.total}
            </span>
          </div>

          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${(progress.current / progress.total) * 100}%` }}></div>
          </div>

          <p className="progress-message">⏳ Temps estimé restant : ~{Math.ceil((progress.total - progress.current + 1) * 3)}s</p>

          {/* Bouton Stop */}
          <button onClick={stopEnrichment} className="btn-stop" disabled={stopRequested}>
            <StopCircle size={20} />
            {stopRequested ? 'Arrêt en cours...' : "Arrêter l'enrichissement"}
          </button>
        </div>
      )}

      {/* Liste des verbes non enrichis */}
      {!enriching && verbes.length > 0 && (
        <div className="verbs-list">
          <h3>📝 Verbes à enrichir :</h3>
          <div className="verbs-grid">
            {verbes.map((verbe) => (
              <div key={verbe.id} className="verb-item">
                <span className="verb-source">
                  {languageFlags[verbe.langue]} {verbe.infinitif}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Verbes enrichis avec succès */}
      {enrichedVerbs.length > 0 && (
        <div className="success-list">
          <h3>✅ Verbes enrichis avec succès :</h3>
          <div className="success-items">
            {enrichedVerbs.map((verbe, index) => (
              <div key={index} className="success-item">
                <CheckCircle size={16} color="#10B981" />
                <span>
                  {languageFlags[verbe.langue]} {verbe.infinitif}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Erreurs */}
      {errors.length > 0 && (
        <div className="errors-list">
          <h3>❌ Erreurs :</h3>
          <div className="error-items">
            {errors.map((error, index) => (
              <div key={index} className="error-item">
                <AlertCircle size={16} color="#EF4444" />
                <div>
                  <strong>
                    {languageFlags[error.verbe.langue]} {error.verbe.infinitif}
                  </strong>
                  <span className="error-message">{error.error}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Message si aucun verbe */}
      {!loading && !enriching && verbes.length === 0 && (
        <div className="no-verbs">
          <CheckCircle size={48} color="#10B981" />
          <h3>Tous les verbes sont enrichis !</h3>
          <p>Faites des traductions pour ajouter de nouveaux verbes.</p>
        </div>
      )}
    </div>
  );
}
